import React, { useState, useRef, useEffect } from 'react';
import { 
  ChevronDown, ChevronUp, Lightbulb, Quote, 
  TrendingUp, AlertCircle, CheckCircle, Info,
  Eye, EyeOff, Bookmark, MessageCircle
} from 'lucide-react';
import { EnhancedAnalysis, ContentSection } from '../../types/enhancedAnalysis';
import MarkdownRenderer from '../diagnosis/MarkdownRenderer';

interface AnalysisContentProps {
  analysis: EnhancedAnalysis;
  activeSection: string;
}

interface TermTooltipProps {
  term: string;
  definition: string;
  children: React.ReactNode;
}

const TermTooltip: React.FC<TermTooltipProps> = ({ term, definition, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <span className="relative inline-block">
      <span
        className="border-b border-dotted border-blue-500 cursor-help text-blue-600"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </span>
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 max-w-xs">
          <div className="font-medium mb-1">{term}</div>
          <div className="text-gray-300">{definition}</div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      )}
    </span>
  );
};

interface SectionCardProps {
  section: ContentSection;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onHighlight: (text: string) => void;
}

const SectionCard: React.FC<SectionCardProps> = ({
  section,
  isActive,
  isExpanded,
  onToggle,
  onHighlight
}) => {
  const getSectionIcon = (type: string) => {
    switch (type) {
      case 'introduction': return <Info className="w-5 h-5" />;
      case 'analysis': return <TrendingUp className="w-5 h-5" />;
      case 'insights': return <Lightbulb className="w-5 h-5" />;
      case 'conclusion': return <CheckCircle className="w-5 h-5" />;
      case 'methodology': return <AlertCircle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getSectionColor = (type: string) => {
    switch (type) {
      case 'introduction': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'analysis': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'insights': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'conclusion': return 'text-green-600 bg-green-50 border-green-200';
      case 'methodology': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div 
      id={section.id}
      data-section={section.id}
      className={`bg-white rounded-xl border-2 transition-all duration-300 ${
        isActive ? 'border-blue-300 shadow-lg' : 'border-gray-200 shadow-sm'
      }`}
    >
      {/* 章节头部 */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-lg ${getSectionColor(section.type)}`}>
              {getSectionIcon(section.type)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                <span>预计阅读 {section.estimatedReadingTime} 分钟</span>
                <span>•</span>
                <span>{section.keyPoints.length} 个要点</span>
              </div>
            </div>
          </div>
          
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isExpanded ? (
              <EyeOff className="w-5 h-5 text-gray-400" />
            ) : (
              <Eye className="w-5 h-5 text-gray-400" />
            )}
          </button>
        </div>

        {/* 关键要点预览 */}
        <div className="mt-4 flex flex-wrap gap-2">
          {section.keyPoints.map((point, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
            >
              {point}
            </span>
          ))}
        </div>
      </div>

      {/* 章节内容 */}
      <div className={`transition-all duration-300 overflow-hidden ${
        isExpanded ? 'max-h-none' : 'max-h-32'
      }`}>
        <div className="p-6">
          <div className="prose prose-lg max-w-none">
            <MarkdownRenderer
              content={section.content}
              className="text-gray-800 leading-relaxed"
              enableSyntaxHighlight={true}
              theme="light"
            />
          </div>

          {/* 视觉元素 */}
          {section.visualElements && section.visualElements.length > 0 && (
            <div className="mt-6 space-y-4">
              {section.visualElements.map((element) => (
                <div key={element.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">{element.title}</span>
                  </div>
                  {element.description && (
                    <p className="text-sm text-gray-600 mb-3">{element.description}</p>
                  )}
                  {element.type === 'image' && (
                    <img
                      src={element.url}
                      alt={element.alt}
                      className="w-full rounded-lg"
                    />
                  )}
                  {element.caption && (
                    <p className="text-xs text-gray-500 mt-2 italic">{element.caption}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 展开/收起提示 */}
        {!isExpanded && (
          <div className="px-6 pb-4">
            <button
              onClick={onToggle}
              className="w-full flex items-center justify-center py-3 text-blue-600 hover:text-blue-700 transition-colors border-t border-gray-100"
            >
              <span className="text-sm font-medium mr-2">展开阅读完整内容</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* 章节底部操作 */}
      {isExpanded && (
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onHighlight(section.content.slice(0, 100))}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
              >
                <Quote className="w-4 h-4" />
                <span>引用</span>
              </button>
              <button className="flex items-center space-x-2 text-sm text-gray-600 hover:text-blue-600 transition-colors">
                <Bookmark className="w-4 h-4" />
                <span>收藏</span>
              </button>
              {/* 移除分享按钮 */}
            </div>
            
            <button
              onClick={onToggle}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              <span>收起</span>
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AnalysisContent: React.FC<AnalysisContentProps> = ({
  analysis,
  activeSection
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['introduction']));
  const [highlightedText, setHighlightedText] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  // 自动展开当前活跃章节
  useEffect(() => {
    if (activeSection && !expandedSections.has(activeSection)) {
      setExpandedSections(prev => new Set([...prev, activeSection]));
    }
  }, [activeSection, expandedSections]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleHighlight = (text: string) => {
    setHighlightedText(text);
    // 这里可以添加复制到剪贴板的逻辑
    navigator.clipboard.writeText(text);
  };

  const expandAllSections = () => {
    setExpandedSections(new Set(analysis.sections.map(s => s.id)));
  };

  const collapseAllSections = () => {
    setExpandedSections(new Set());
  };

  return (
    <div ref={contentRef} className="space-y-8">
      {/* 内容控制栏 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900">深度分析内容</h3>
            <span className="text-sm text-gray-500">
              {analysis.sections.length} 个章节 • {analysis.wordCount.toLocaleString()} 字
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={expandAllSections}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              全部展开
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={collapseAllSections}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 transition-colors"
            >
              全部收起
            </button>
          </div>
        </div>
      </div>

      {/* 章节内容 */}
      <div className="space-y-6">
        {analysis.sections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <SectionCard
              key={section.id}
              section={section}
              isActive={activeSection === section.id}
              isExpanded={expandedSections.has(section.id)}
              onToggle={() => toggleSection(section.id)}
              onHighlight={handleHighlight}
            />
          ))}
      </div>

      {/* 关键洞察卡片 */}
      {analysis.keyInsights.length > 0 && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">关键洞察</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.keyInsights.map((insight) => (
              <div key={insight.id} className="bg-white rounded-lg p-4 border border-yellow-200">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{insight.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    insight.importance === 'high' ? 'bg-red-100 text-red-800' :
                    insight.importance === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {insight.importance === 'high' ? '高' :
                     insight.importance === 'medium' ? '中' : '低'}
                  </span>
                </div>
                <p className="text-gray-700 text-sm mb-3">{insight.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{insight.category}</span>
                  {/* 移除分享洞察按钮 */}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 实用技巧 */}
      {analysis.practicalTips.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">实用技巧</h3>
          </div>
          
          <div className="space-y-3">
            {analysis.practicalTips.map((tip) => (
              <div key={tip.id} className="bg-white rounded-lg p-4 border border-green-200">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{tip.title}</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      tip.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                      tip.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {tip.difficulty === 'beginner' ? '入门' :
                       tip.difficulty === 'intermediate' ? '进阶' : '专家'}
                    </span>
                    <span className="text-xs text-gray-500">{tip.estimatedTime}分钟</span>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 高亮文本显示 */}
      {highlightedText && (
        <div className="fixed bottom-4 right-4 bg-gray-900 text-white p-4 rounded-lg shadow-lg max-w-sm z-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">已复制到剪贴板</span>
            <button
              onClick={() => setHighlightedText('')}
              className="text-gray-400 hover:text-white"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-300 truncate">{highlightedText}</p>
        </div>
      )}
    </div>
  );
};

export default AnalysisContent;